import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { airHandlerUnitApi } from "~/api";
import { getAirHandlerUnitsGroups } from "~/features/air-handler-unit";
import { buildGroupsUniqueUpdateRequestBody, Groups, type Group } from "~/features/settings";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/bms/ahu-groups")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, floors, airHandlerUnits } = useBuildingContext(Route.id);

  const { mutate } = useMutation({
    mutationFn: (updates: { airHandlerUnitId: string; group: string | null }[]) =>
      Promise.all(
        updates.map(({ airHandlerUnitId, group }) =>
          airHandlerUnitApi.update(building.id, airHandlerUnitId, { group }),
        ),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: airHandlerUnitApi.queries._def });
    },
  });

  const groups = getAirHandlerUnitsGroups(airHandlerUnits);

  const handleGroupsChange = async (updatedGroups: Group[]): Promise<void> => {
    const groupsUpdate = buildGroupsUniqueUpdateRequestBody(updatedGroups, groups);
    const body = groupsUpdate.flatMap(g =>
      g.itemIds.map(airHandlerUnitId => ({ airHandlerUnitId, group: g.group ?? null })),
    );
    return new Promise((resolve, reject) => {
      mutate(body, {
        onSuccess: () => resolve(),
        onError: error => reject(error),
      });
    });
  };

  return (
    <div className="h-full" data-testid="AhuGroupsSettings">
      <Groups
        buildingId={building.id}
        floors={floors}
        airHandlerUnits={airHandlerUnits}
        groups={groups}
        onGroupsChange={handleGroupsChange}
      />
    </div>
  );
}
